/* eslint-disable @stylistic/brace-style */
/* eslint-disable @stylistic/arrow-parens */
import {
    ActionReference,
    DefaultGameState,
    GameObjectReference,
    GameState,
    // eslint-disable-next-line @stylistic/comma-dangle
    PlayerSession,
} from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        background: linear-gradient(114.59deg, #000000 -5.52%, #730f2a 100%);
        font-size: 16px;
    }

    .canvas-container {
        background-color: black;
        width: 80%;
        max-width: 900px;
        height: 80vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .header {
        width: 100%;
        height: 60%;
        position: relative;
    }

    .header img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .content {
        display: flex;
        justify-content: space-between;
        padding: 20px;
        gap: 20px;
        max-height: 20%;
        height: 20%;
    }

    .content .text {
        flex-grow: 1;
        overflow-y: auto;
        color: white;
        width: 70%;
    }

    .content .object-image {
        aspect-ratio: 1;
        background-color: #ff5482;
    }

    .content #text-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .divider {
        height: 1px;
        background-color: #ffffff;
        width: calc(100% - 40px);
        margin: 0 auto; /* Center it */
    }

    .footer {
        padding: 20px;
        display: flex;
        justify-content: center;
        gap: 10px;
    }

    .footer .button {
        background-color: #ff5482;
        border: 1px solid rgb(216, 52, 96);
        padding: 8px 16px;
        text-transform: uppercase;
        cursor: pointer;
        user-select: none;
        color: white;
        font-family: "Onesize";
        font-size: 16px;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #037a76;
    }

    .restart-button {
        position: fixed;
        bottom: 10px;
        right: 10px;
        background-color: #ff5482;
        border: 1px solid rgb(216, 52, 96);
        padding: 8px 16px;
        text-transform: uppercase;
        cursor: pointer;
        user-select: none;
        color: white;
        font-family: "Onesize";
        font-size: 16px;
        z-index: 1000;
    }

    .honey-game {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .honey-game canvas {
        border: 4px solidrgb(148, 103, 41);
        border-radius: 50%;
        background-color: #b98235;
    }
`;

export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    /** Current player session */
    public _playerSession?: PlayerSession;

    public _honeyGameWon: boolean = false;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshGameState();
    }

    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        await this.updateGameState(state);
    }

    private async updateGameState(state: GameState): Promise<void> {
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);
            return;
        }
        this._playerSession = await this._gameRouteService.getPlayerSession();

        this._currentGameState = state;
        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();
        this.render();
    }

    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
            <div class="canvas-container">
                ${this.renderHeader()}
                ${this.renderContent()}
                <div class="divider"></div>
                ${this.renderFooter()}
            </div>
            ${this.renderRestartButton()}
            ${
                this._playerSession?.honeyGame && !this._honeyGameWon
                    ? this.renderHoneyGame()
                    : html`<div></div>`
            }
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }
        this.shadowRoot.append(...elements);

        this.afterRender();
    }

    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;
        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    <img src="/assets/img/rooms/${roomImages[0]}.png" />
                </div>
            `;
        }
        return "";
    }

    private renderContent(): string {
        return `
            <div class="content">
                <div class="text" id="text-container"></div>
                <div class="object-image"></div>
            </div>
        `;
    }

    private afterRender(): void {
        const textContainer: HTMLElement | null | undefined =
            this.shadowRoot?.getElementById("text-container");
        if (textContainer && this._currentGameState?.text) {
            this.typeText(textContainer, this._currentGameState.text, 10);
        }
    }

    private typeText(container: HTMLElement, textArray: string[], speed = 10): void {
        container.innerHTML = "";
        let paragraphIndex: number = 0;

        const typeNextParagraph: () => void = () => {
            if (paragraphIndex < textArray.length) {
                const paragraph: HTMLDivElement = document.createElement("div");
                paragraph.classList.add("text-paragraph");
                container.appendChild(paragraph);

                let charIndex: number = 0;
                const text: string = textArray[paragraphIndex];

                const interval: ReturnType<typeof setInterval> = setInterval(() => {
                    if (charIndex < text.length) {
                        paragraph.innerHTML += text[charIndex];
                        charIndex++;
                        container.scrollTop = container.scrollHeight;
                    } else {
                        clearInterval(interval);
                        paragraphIndex++;
                        typeNextParagraph();
                    }
                }, speed);
            }
        };

        typeNextParagraph();
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        // Check if _selectedActionButton is true
        if (this._selectedActionButton) {
            // eslint-disable-next-line @typescript-eslint/typedef
            const footer = html`
                <div class="footer">
                    <button class="button">Back</button>
                    ${this._currentGameState?.objects.map((button) => this.renderGameObjectButton(button))}
                </div>
            `;

            const backButton: HTMLButtonElement | null = footer.querySelector("button");
            if (backButton) {
                backButton.addEventListener("click", () => {
                    this._selectedActionButton = undefined;
                    this.render();
                });
            }

            return footer;
        } else {
            return html`
                <div class="footer">
                    <!-- Show action buttons when _selectedActionButton is not true -->
                    ${this._currentGameState?.actions.map((button) => this.renderActionButton(button))}
                </div>
            `;
        }
    }

    private renderRestartButton(): HTMLElement {
        const element: HTMLElement = html` <a class="restart-button">Restart</a> `;

        element.addEventListener("click", () => {
            // clear the local storage and refresh page
            localStorage.clear();
            window.location.reload();
        });

        return element;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}"> ${button.name} </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        await this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map((e) => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        await this.updateGameState(state);
    }

    private renderHoneyGame(): HTMLElement {
        const element: HTMLElement = html`
            <div class="honey-game">
                <canvas id="circleCanvas" width="300" height="300"></canvas>
            </div>
        `;

        const canvas: HTMLCanvasElement | null = element.querySelector("#circleCanvas") as HTMLCanvasElement;
        const context: CanvasRenderingContext2D | null = canvas.getContext("2d");

        if (context !== null) {
            const centerX: number = 150;
            const centerY: number = 150;
            const outerRadius: number = 120;
            const innerRadius: number = 80;

            const clickedAngles: number[] = [];
            const totalSections: number = 12;
            const sectionArc: number = (Math.PI * 2) / totalSections;

            function drawRing(this: CanvasComponent): void {
                if (context && canvas) {
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.beginPath();
                    context.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
                    context.lineWidth = 30;
                    context.strokeStyle = "black";
                    context.stroke();

                    clickedAngles.forEach((angle) => {
                        context.beginPath();
                        context.arc(
                            centerX,
                            centerY,
                            outerRadius,
                            angle - sectionArc / 2,
                            angle + sectionArc / 2
                        ); // Draw each section
                        context.lineWidth = 30;
                        context.strokeStyle = "rgb(216, 52, 96)";
                        context.stroke();
                    });
                }

                if (isRingComplete()) {
                    this._honeyGameWon = true;
                    this.render();
                }
            }

            drawRing.call(this);

            function isMouseInsideRing(x: number, y: number): boolean {
                const distance: number = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
                return distance >= innerRadius + 20 && distance <= outerRadius + 20;
            }

            function getAngle(x: number, y: number): number {
                return Math.atan2(y - centerY, x - centerX);
            }

            function isRingComplete(): boolean {
                if (clickedAngles.length < totalSections) return false;

                const sortedAngles: number[] = [...clickedAngles].sort((a, b) => a - b);

                for (let i: number = 0; i < sortedAngles.length; i++) {
                    const currentAngle: number = sortedAngles[i];
                    const nextAngle: number = sortedAngles[(i + 1) % sortedAngles.length];
                    const angleDiff: number = (nextAngle - currentAngle + Math.PI * 2) % (Math.PI * 2);
                    if (angleDiff > sectionArc) {
                        return false;
                    }
                }

                return true;
            }

            canvas.addEventListener("mousemove", (event: MouseEvent) => {
                const rect: DOMRect = canvas.getBoundingClientRect();
                const mouseX: number = event.clientX - rect.left;
                const mouseY: number = event.clientY - rect.top;

                drawRing.call(this);

                if (isMouseInsideRing(mouseX, mouseY)) {
                    context.beginPath();
                    context.arc(mouseX, mouseY, 10, 0, Math.PI * 2);
                    context.fillStyle = "rgba(216, 52, 96, 0.5)";
                    context.fill();
                } else {
                    clickedAngles.length = 0;
                    drawRing.call(this);
                }
            });

            canvas.addEventListener("click", (event: MouseEvent) => {
                const rect: DOMRect = canvas.getBoundingClientRect();
                const mouseX: number = event.clientX - rect.left;
                const mouseY: number = event.clientY - rect.top;

                if (isMouseInsideRing(mouseX, mouseY)) {
                    const angle: number = getAngle(mouseX, mouseY);

                    if (
                        !clickedAngles.some((clickedAngle) => Math.abs(clickedAngle - angle) < sectionArc / 2)
                    ) {
                        clickedAngles.push(angle);
                        drawRing.call(this);
                    }
                }
            });
        }

        return element;
    }
}
